import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useEquipment } from '../../providers/EquipmentProvider';
import { useEquipmentStore } from '../../shared/services/equipment/hooks';
import { EDITOR_ONBOARDING_STEPS } from './editorOnboardingSteps';
import {
  dispatchOnboardingPumpTooltipOpen,
  releaseOnboardingSceneControl,
  setEditorOnboardingSceneDemoReady,
  setEditorOnboardingTourActive,
} from './onboardingSceneControl';
import {
  buildOnboardingShowcaseSceneOverlay,
  pickOnboardingShowcasePump,
} from './ensureOnboardingShowcasePump';
import { CalculationsAPI } from '../../shared/services/calculations/api';
import '../../styles/features/onboarding/editor-onboarding-tour.css';

const CARD_MAX_W = 360;
const CARD_MARGIN = 16;
const HOLE_PAD = 10;
const SITE_CONTROL_HOLE_TOP_EXTRA = 125;
const SITE_CONTROL_HOLE_SIDE_EXTRA = 10;
const SITE_CONTROL_CARD_LIFT_PX = 136;
const POOL_PARAMS_INTRO_CARD_RIGHT_SHIFT_PX = 20;
const EQUIPMENT_PANEL_CARD_LEFT_SHIFT_PX = 10;
const EQUIPMENT_PANEL_ESTIMATED_RIGHT_PX = 355;
const AI_ASSISTANT_HOLE_PAD = 4;
const AI_ASSISTANT_CARD_RIGHT_SHIFT_PX = 10;
const SCENE_FOCUS_CARD_GAP_PX = 20;

const CONFETTI_COLORS = ['#38bdf8','#7dd3fc','#0ea5e9','#e0f2fe','#bae6fd','#ffffff','#60a5fa','#93c5fd'];
const CONFETTI_PIECES = Array.from({ length: 20 }, (_, i) => ({
  id: i,
  color: CONFETTI_COLORS[i % 8],
  left: 4 + i * 4.8,
  delay: i * 55,
  size: 6 + (i % 3) * 3,
  round: i % 3 === 0,
  speed: 1300 + (i % 4) * 250,
}));

/* ── Inline SVG icons for tour step cards ─────────────────────────────── */
const TOUR_STEP_SVG_PATHS = {
  sliders: (
    <>
      <line x1="21" x2="14" y1="4" y2="4"/>
      <line x1="10" x2="3" y1="4" y2="4"/>
      <line x1="21" x2="12" y1="12" y2="12"/>
      <line x1="8" x2="3" y1="12" y2="12"/>
      <line x1="21" x2="16" y1="20" y2="20"/>
      <line x1="12" x2="3" y1="20" y2="20"/>
      <circle cx="12" cy="4" r="2"/>
      <circle cx="10" cy="12" r="2"/>
      <circle cx="14" cy="20" r="2"/>
    </>
  ),
  'layout-dashboard': (
    <>
      <rect width="7" height="9" x="3" y="3" rx="1"/>
      <rect width="7" height="5" x="14" y="3" rx="1"/>
      <rect width="7" height="9" x="14" y="12" rx="1"/>
      <rect width="7" height="5" x="3" y="16" rx="1"/>
    </>
  ),
  package: (
    <>
      <path d="m7.5 4.27 9 5.15"/>
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
      <path d="m3.3 7 8.7 5 8.7-5"/>
      <path d="M12 22V12"/>
    </>
  ),
  bot: (
    <>
      <path d="M12 8V4H8"/>
      <rect width="16" height="12" x="4" y="8" rx="2"/>
      <path d="M2 14h2"/><path d="M20 14h2"/>
      <path d="M15 13v2"/><path d="M9 13v2"/>
    </>
  ),
  box: (
    <>
      <path d="M21 8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16Z"/>
      <path d="m3.3 7 8.7 5 8.7-5"/>
      <path d="M12 22V12"/>
    </>
  ),
};

function TourStepIcon({ id }) {
  const paths = TOUR_STEP_SVG_PATHS[id];
  if (!paths) return null;
  return (
    <svg
      className="editor-onboarding-tour__step-icon-svg"
      xmlns="http://www.w3.org/2000/svg"
      width={20} height={20}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
      focusable="false"
    >
      {paths}
    </svg>
  );
}

/* ── Pointer arrow ────────────────────────────────────────────────────────── */
function TourPointerIcon({ className }) {
  return (
    <svg
      className={className}
      xmlns="http://www.w3.org/2000/svg"
      width={22} height={22}
      viewBox="0 0 24 24"
      fill="currentColor"
      stroke="none"
      aria-hidden
      focusable="false"
    >
      <path d="M5 5a2 2 0 0 1 3.008-1.728l11.997 6.998a2 2 0 0 1 .003 3.458l-12 7A2 2 0 0 1 5 19z" />
    </svg>
  );
}

function EditorOnboardingTourPointer({ side }) {
  if (!side) return null;
  return (
    <span
      className={'editor-onboarding-tour__pointer editor-onboarding-tour__pointer--' + side}
      aria-hidden="true"
    >
      <TourPointerIcon className="editor-onboarding-tour__pointer-icon" />
    </span>
  );
}

/* ── Celebration overlay ─────────────────────────────────────────────────── */
function CelebrationOverlay({ onDone }) {
  return (
    <div className="tour-celebrate" role="dialog" aria-modal="true" aria-label="Тур завершён">
      <div className="tour-celebrate__confetti-wrap" aria-hidden="true">
        {CONFETTI_PIECES.map(p => (
          <div
            key={p.id}
            className="tour-celebrate__confetti-piece"
            style={{
              left: p.left + '%',
              width: p.size,
              height: p.size,
              borderRadius: p.round ? '50%' : '3px',
              backgroundColor: p.color,
              animationDelay: p.delay + 'ms',
              animationDuration: p.speed + 'ms',
            }}
          />
        ))}
      </div>

      <div className="tour-celebrate__card">
        <div className="tour-celebrate__check-wrap">
          <svg
            className="tour-celebrate__check-svg"
            viewBox="0 0 56 56"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            aria-hidden="true"
          >
            <circle
              className="tour-celebrate__check-circle"
              cx="28" cy="28" r="26"
              stroke="#38bdf8"
              strokeWidth="2"
            />
            <path
              className="tour-celebrate__check-path"
              d="M18 28l7 7 13-13"
              stroke="#38bdf8"
              strokeWidth="2.5"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <div className="tour-celebrate__check-glow" aria-hidden="true" />
        </div>

        <h2 className="tour-celebrate__title">Вы готовы к работе!</h2>
        <p className="tour-celebrate__subtitle">Настройте ваш первый проект</p>

        <button
          type="button"
          className="tour-celebrate__btn"
          onClick={onDone}
        >
          Начать проект →
        </button>
      </div>
    </div>
  );
}

/* ── Shade quad (with transitions for smooth hole movement) ─────────────── */
function tourCardPointerSide(placementClass) {
  if (!placementClass || placementClass.includes('place-center')) return null;
  if (placementClass.includes('place-top'))    return 'top';
  if (placementClass.includes('place-bottom')) return 'bottom';
  if (placementClass.includes('place-right'))  return 'right';
  if (placementClass.includes('place-left'))   return 'left';
  return null;
}

function ensureEquipmentPanelExpanded() {
  const aside = document.getElementById('equipment-panel');
  if (!aside?.classList.contains('collapsed')) return;
  // Add class to <body> — React cannot overwrite it when it re-renders the panel,
  // so the CSS `body.onboarding-panel-instant #equipment-panel { transition: none }` stays
  // in effect throughout the React re-render cycle, preventing the slide animation.
  document.body.classList.add('onboarding-panel-instant');
  aside.querySelector('.equipment-panel-toggle')?.click();
  requestAnimationFrame(() => {
    requestAnimationFrame(() => { document.body.classList.remove('onboarding-panel-instant'); });
  });
}

function measureTarget(selector) {
  const el = document.querySelector(selector);
  if (!el) return null;
  const r = el.getBoundingClientRect();
  if (r.width < 2 && r.height < 2) return null;
  return r;
}

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function holeRectsEqual(a, b, epsilon = 0.5) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.top - b.top) < epsilon &&
    Math.abs(a.left - b.left) < epsilon &&
    Math.abs(a.width - b.width) < epsilon &&
    Math.abs(a.height - b.height) < epsilon
  );
}

function cardStylesEqual(a, b, epsilon = 0.5) {
  if (a === b) return true;
  if (!a || !b) return false;
  return (
    Math.abs(a.top - b.top) < epsilon &&
    Math.abs(a.left - b.left) < epsilon &&
    a.transform === b.transform &&
    (a.right ?? 'auto') === (b.right ?? 'auto')
  );
}

function TourShades({ hole }) {
  if (!hole) {
    return <div className="editor-onboarding-tour__shade-full" aria-hidden="true" />;
  }
  const { top, left, width, height } = hole;
  return (
    <>
      <div className="editor-onboarding-tour__shade" style={{ top: 0, left: 0, right: 0, height: Math.max(0, top) }} />
      <div className="editor-onboarding-tour__shade" style={{ top, left: 0, width: Math.max(0, left), height }} />
      <div className="editor-onboarding-tour__shade" style={{ top, left: left + width, right: 0, height }} />
      <div className="editor-onboarding-tour__shade" style={{ top: top + height, left: 0, right: 0, bottom: 0 }} />
      <div className="editor-onboarding-tour__hole-blocker" style={{ top, left, width, height }} aria-hidden="true" />
    </>
  );
}

/* ── Main component ───────────────────────────────────────────────────────── */
export function EditorOnboardingTour({ open, onComplete, persistComplete, storageKey }) {
  const steps = EDITOR_ONBOARDING_STEPS;
  const [index, setIndex] = useState(0);
  const [celebrating, setCelebrating] = useState(false);
  const tourFinishedRef = useRef(false);
  const [hole, setHole] = useState(null);
  const [cardStyle, setCardStyle] = useState({ top: 0, left: 0 });
  const [placementClass, setPlacementClass] = useState('editor-onboarding-tour__card--place-right');
  const cardRef = useRef(null);
  const catalogEquipmentRef = useRef([]);
  const layoutFrameRef = useRef(null);
  const { setOnboarding3dSceneOverlay } = useEquipment();
  const { equipment: catalogEquipment, getPumps, isDataLoaded } = useEquipmentStore();

  catalogEquipmentRef.current = catalogEquipment;

  const step   = steps[index];
  const isLast = index >= steps.length - 1;

  const updateLayout = useCallback(() => {
    if (!open || !step || celebrating) return;
    if (step.id === 'scene-3d') {
      const sceneEl = document.querySelector('#scene-3d-viewport');
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const sr = sceneEl?.getBoundingClientRect();
      const box =
        sr && sr.width >= 8 && sr.height >= 8
          ? sr
          : { left: 0, top: 0, width: vw, height: vh };

      // Большое «окно» на почти всю сцену: демо-насос виден целиком, есть простор
      // чтобы его покрутить. Сверху оставляем полосу под карточку-подсказку.
      const sceneTop = box.top;
      const TOP_BAND = box.height * 0.2;   // место под карточку сверху
      const BOTTOM_GAP = box.height * 0.02;
      const holeW = box.width * 0.96;
      const holeLeft = box.left + (box.width - holeW) / 2;
      const holeTop = sceneTop + TOP_BAND;
      const holeH = Math.max(box.height * 0.4, box.height - TOP_BAND - BOTTOM_GAP);

      const nextHole = { top: Math.max(0, holeTop), left: Math.max(0, holeLeft), width: holeW, height: holeH };
      setHole(prev => (holeRectsEqual(prev, nextHole) ? prev : nextHole));

      const cardEl = cardRef.current;
      const ch = cardEl?.offsetHeight || 200;
      const holeCX = holeLeft + holeW / 2;
      const top = clamp(holeTop - SCENE_FOCUS_CARD_GAP_PX - ch / 2, CARD_MARGIN + ch / 2, vh - CARD_MARGIN - ch / 2);

      const nextCardStyle = { top, left: holeCX, transform: 'translate(-50%, -50%)', right: 'auto' };
      setCardStyle(prev => (cardStylesEqual(prev, nextCardStyle) ? prev : nextCardStyle));
      setPlacementClass(prev =>
        prev === 'editor-onboarding-tour__card--scene-focus editor-onboarding-tour__card--place-top'
          ? prev
          : 'editor-onboarding-tour__card--scene-focus editor-onboarding-tour__card--place-top'
      );
      return;
    }

    const r = measureTarget(step.targetSelector);

    // equipment-panel is panel-right (position:fixed; right:0).
    // r.width is unaffected by the collapsed translateX transform, so it's always reliable.
    // Hole goes on the RIGHT side; card goes to the LEFT of the panel.
    if (step.id === 'equipment-panel') {
      const vpW    = window.innerWidth;
      const vpH    = window.innerHeight;
      const cardEl = cardRef.current;
      const cardW  = Math.min(CARD_MAX_W, cardEl?.offsetWidth || 320);
      const cardH  = cardEl?.offsetHeight || 170;

      const panelW    = (r && r.width > 50) ? r.width : EQUIPMENT_PANEL_ESTIMATED_RIGHT_PX;
      const panelLeft = vpW - panelW;

      const nextHole = { top: 0, left: Math.max(0, panelLeft - HOLE_PAD), width: panelW + HOLE_PAD, height: vpH };
      setHole(prev => (holeRectsEqual(prev, nextHole) ? prev : nextHole));

      const cardLeft = clamp(panelLeft - CARD_MARGIN - cardW / 2, CARD_MARGIN + cardW / 2, vpW - CARD_MARGIN - cardW / 2);
      const cardTop  = clamp(vpH * 0.35, CARD_MARGIN + cardH / 2, vpH - CARD_MARGIN - cardH / 2);

      const nextCardStyle = { top: cardTop, left: cardLeft, transform: 'translate(-50%, -50%)' };
      setCardStyle(prev => (cardStylesEqual(prev, nextCardStyle) ? prev : nextCardStyle));
      setPlacementClass(prev => {
        const cls = 'editor-onboarding-tour__card--place-left';
        return prev === cls ? prev : cls;
      });
      return;
    }

    if (!r) {
      setHole(prev => (prev === null ? prev : null));
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const centerCard = { top: vh / 2, left: vw / 2, transform: 'translate(-50%, -50%)' };
      setCardStyle(prev => (cardStylesEqual(prev, centerCard) ? prev : centerCard));
      setPlacementClass(prev =>
        prev === 'editor-onboarding-tour__card--place-center' ? prev : 'editor-onboarding-tour__card--place-center'
      );
      return;
    }

    const holePad  = step.id === 'ai-assistant' ? AI_ASSISTANT_HOLE_PAD : HOLE_PAD;
    const topExtra = step.id === 'site-control'  ? SITE_CONTROL_HOLE_TOP_EXTRA : 0;
    const sideExtra= step.id === 'site-control'  ? SITE_CONTROL_HOLE_SIDE_EXTRA : 0;
    const nextHole = {
      top:    r.top  - holePad - topExtra,
      left:   r.left - holePad - sideExtra,
      width:  r.width  + holePad * 2 + sideExtra * 2,
      height: r.height + holePad * 2 + topExtra,
    };
    setHole(prev => (holeRectsEqual(prev, nextHole) ? prev : nextHole));

    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const cardEl = cardRef.current;
    const cw = Math.min(CARD_MAX_W, cardEl?.offsetWidth || 320);
    const ch = cardEl?.offsetHeight || 140;

    let top  = r.top  + r.height / 2;
    let left = r.left + r.width  / 2;
    let pClass = 'editor-onboarding-tour__card--place-right';

    if (step.placement === 'right') {
      const rightShift = step.id === 'pool-params-intro' ? POOL_PARAMS_INTRO_CARD_RIGHT_SHIFT_PX : 0;
      left = r.right + CARD_MARGIN + cw / 2 + rightShift;
      top  = r.top   + r.height / 2;
      if (left + cw / 2 > vw - CARD_MARGIN) {
        left   = r.left - CARD_MARGIN - cw / 2;
        pClass = 'editor-onboarding-tour__card--place-left';
      } else {
        pClass = 'editor-onboarding-tour__card--place-right';
      }
    } else if (step.placement === 'left') {
      const leftShift = step.id === 'equipment-panel' ? EQUIPMENT_PANEL_CARD_LEFT_SHIFT_PX : 0;
      left = r.left - CARD_MARGIN - cw / 2 - leftShift;
      top  = r.top  + r.height / 2;
      if (left - cw / 2 < CARD_MARGIN) {
        left   = r.right + CARD_MARGIN + cw / 2;
        pClass = 'editor-onboarding-tour__card--place-right';
      } else {
        pClass = 'editor-onboarding-tour__card--place-left';
      }
    } else if (step.placement === 'top') {
      const lift = step.id === 'site-control' ? SITE_CONTROL_CARD_LIFT_PX : 0;
      top  = r.top  - CARD_MARGIN - ch / 2 - lift;
      left = r.left + r.width / 2;
      pClass = 'editor-onboarding-tour__card--place-top';
      if (top - ch / 2 < CARD_MARGIN) {
        top    = r.bottom + CARD_MARGIN + ch / 2;
        pClass = 'editor-onboarding-tour__card--place-bottom';
      }
    }
    if (step.id === 'ai-assistant') left += AI_ASSISTANT_CARD_RIGHT_SHIFT_PX;

    top  = clamp(top,  CARD_MARGIN + ch / 2, vh - CARD_MARGIN - ch / 2);
    left = clamp(left, CARD_MARGIN + cw / 2, vw - CARD_MARGIN - cw / 2);

    const nextCardStyle = { top, left, transform: 'translate(-50%, -50%)' };
    setCardStyle(prev => (cardStylesEqual(prev, nextCardStyle) ? prev : nextCardStyle));
    setPlacementClass(prev => (prev === pClass ? prev : pClass));
  }, [open, step, celebrating]);

  const scheduleLayoutUpdate = useCallback(() => {
    if (layoutFrameRef.current != null) return;
    layoutFrameRef.current = requestAnimationFrame(() => {
      layoutFrameRef.current = null;
      updateLayout();
    });
  }, [updateLayout]);

  useLayoutEffect(() => { if (!open) return; updateLayout(); }, [open, index, updateLayout]);

  useEffect(() => {
    if (!open) return;
    const raf = requestAnimationFrame(() => updateLayout());
    const t   = window.setTimeout(updateLayout, 280);
    return () => { cancelAnimationFrame(raf); window.clearTimeout(t); };
  }, [open, index, updateLayout]);

  useEffect(() => {
    if (!open) return;
    const onResize = () => scheduleLayoutUpdate();
    window.addEventListener('resize', onResize);
    window.addEventListener('scroll', onResize, true);
    const ro = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(() => scheduleLayoutUpdate()) : null;
    const el = step ? document.querySelector(step.targetSelector) : null;
    if (ro && el) ro.observe(el);
    return () => {
      window.removeEventListener('resize', onResize);
      window.removeEventListener('scroll', onResize, true);
      ro?.disconnect();
      if (layoutFrameRef.current != null) { cancelAnimationFrame(layoutFrameRef.current); layoutFrameRef.current = null; }
    };
  }, [open, index, step, scheduleLayoutUpdate]);

  /* Freeze #equipment-panel transition for the whole tour to prevent slide animation */
  useEffect(() => {
    if (!open) {
      document.body.classList.remove('editor-tour-open');
      return;
    }
    document.body.classList.add('editor-tour-open');
    return () => { document.body.classList.remove('editor-tour-open'); };
  }, [open]);

  useEffect(() => {
    if (!open || !step) return;
    const el = document.querySelector(step.targetSelector);
    // Skip scrollIntoView for fixed-position panels — they are always in viewport
    // and scroll attempts on transformed fixed elements cause unexpected layout shifts
    if (step.id !== 'equipment-panel') {
      el?.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
    }
    if (step.expandEquipmentPanel) {
      ensureEquipmentPanelExpanded();
      window.setTimeout(updateLayout, 320);
    }
  }, [open, index, step, updateLayout]);

  useEffect(() => {
    if (!open || step?.id !== 'scene-3d') return undefined;
    const ids = [450, 900, 1400].map(ms => window.setTimeout(() => updateLayout(), ms));
    return () => ids.forEach(id => window.clearTimeout(id));
  }, [open, step?.id, updateLayout]);

  /* Pulse ring on target element */
  useEffect(() => {
    if (!open || !step?.targetSelector || !step.showPulse) return undefined;
    const el = document.querySelector(step.targetSelector);
    if (!el) return undefined;
    el.classList.add('onboarding-ring-pulse');
    return () => el.classList.remove('onboarding-ring-pulse');
  }, [open, index, step?.targetSelector, step?.showPulse]);

  useLayoutEffect(() => {
    setEditorOnboardingTourActive(open);
    if (!open) return undefined;
    return () => { releaseOnboardingSceneControl(); };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, [open]);

  useEffect(() => {
    if (!open) { tourFinishedRef.current = false; setIndex(0); setCelebrating(false); }
  }, [open]);

  useEffect(() => {
    const cls = 'welland-onboarding-site-control-step';
    if (!open || step?.id !== 'site-control') {
      document.body.classList.remove(cls);
      return () => document.body.classList.remove(cls);
    }
    document.body.classList.add(cls);
    return () => document.body.classList.remove(cls);
  }, [open, step?.id]);

  useEffect(() => {
    if (!open || step?.id !== 'scene-3d' || !isDataLoaded) return undefined;
    let cancelled = false;
    const tooltipTimers = [];

    function schedulePumpTooltipOpen(delayMs) {
      const id = window.setTimeout(() => { if (!cancelled) dispatchOnboardingPumpTooltipOpen(); }, delayMs);
      tooltipTimers.push(id);
    }

    (async () => {
      const pumps = getPumps();
      const pump  = pickOnboardingShowcasePump(pumps);
      if (!pump || cancelled) return;

      let selectedSand = [];
      if (pump.isMonoblock) {
        try { selectedSand = await CalculationsAPI.getMonoblockSandInfo(pump, catalogEquipmentRef.current); }
        catch { selectedSand = []; }
      }
      if (cancelled) return;

      const overlay = buildOnboardingShowcaseSceneOverlay(pump, selectedSand);
      if (overlay) { setOnboarding3dSceneOverlay(overlay); setEditorOnboardingSceneDemoReady(true); }
      schedulePumpTooltipOpen(500); schedulePumpTooltipOpen(1200);
      schedulePumpTooltipOpen(2200); schedulePumpTooltipOpen(3500);
    })();

    return () => { cancelled = true; tooltipTimers.forEach(id => window.clearTimeout(id)); };
  }, [open, step?.id, isDataLoaded, getPumps, setOnboarding3dSceneOverlay]);

  useEffect(() => {
    if (step?.id === 'scene-3d') return;
    setEditorOnboardingSceneDemoReady(false);
    setOnboarding3dSceneOverlay(null);
  }, [step?.id, setOnboarding3dSceneOverlay]);

  const finishTour = useCallback(() => {
    if (persistComplete && storageKey) {
      try { localStorage.setItem(storageKey, '1'); } catch { /* ignore */ }
    }
    onComplete?.();
  }, [persistComplete, storageKey, onComplete]);

  const handleNext = useCallback(() => {
    if (tourFinishedRef.current) return;
    if (isLast) {
      tourFinishedRef.current = true;
      releaseOnboardingSceneControl();
      setOnboarding3dSceneOverlay(null);
      setCelebrating(true);
      return;
    }
    setIndex(i => Math.min(i + 1, steps.length - 1));
  }, [isLast, setOnboarding3dSceneOverlay, steps.length]);

  const handleSkip = useCallback(() => {
    if (tourFinishedRef.current) return;
    tourFinishedRef.current = true;
    releaseOnboardingSceneControl();
    setOnboarding3dSceneOverlay(null);
    finishTour();
  }, [setOnboarding3dSceneOverlay, finishTour]);

  if (!open || !step) return null;

  const pointerSide = tourCardPointerSide(placementClass);

  const cardClasses = [
    'editor-onboarding-tour__card',
    placementClass,
    step.id === 'site-control'      ? 'editor-onboarding-tour__card--site-control-step'      : '',
    step.id === 'pool-params-intro' ? 'editor-onboarding-tour__card--pool-params-intro-step' : '',
    step.id === 'equipment-panel'   ? 'editor-onboarding-tour__card--equipment-panel-step'   : '',
    step.id === 'ai-assistant'      ? 'editor-onboarding-tour__card--ai-assistant-step'       : '',
  ].filter(Boolean).join(' ');

  return (
    <div className="editor-onboarding-tour" data-step={step?.id} role="dialog" aria-modal="true" aria-live="polite">
      {/* На шаге сцены НЕ затемняем и НЕ вырезаем «окно» — показываем сцену
          целиком (модель + свободное вращение), остаётся только карточка-подсказка
          и правая плашка с информацией. */}
      {!celebrating && step?.id !== 'scene-3d' && <TourShades hole={hole} />}

      {/* key={index} forces remount => CSS entrance animation on each step */}
      <div ref={cardRef} className={cardClasses} style={{ ...cardStyle, display: celebrating ? 'none' : undefined }} key={index}>
        {/* Step header: icon + label */}
        <div className="editor-onboarding-tour__step-header">
          <span className="editor-onboarding-tour__step-icon-wrap">
            <TourStepIcon id={step.iconId} />
          </span>
          <span className="editor-onboarding-tour__step-label">{step.label}</span>
        </div>

        {/* Title */}
        <h3 className="editor-onboarding-tour__title">{step.title}</h3>

        {/* Body text */}
        <p className="editor-onboarding-tour__text">{step.body}</p>

        {/* Footer */}
        <div className="editor-onboarding-tour__footer">
          {/* Progress segments */}
          <div className="editor-onboarding-tour__segments" aria-hidden="true">
            {steps.map((_, i) => (
              <div
                key={i}
                className={'editor-onboarding-tour__segment' + (i <= index ? ' editor-onboarding-tour__segment--done' : '')}
              />
            ))}
          </div>

          {/* Action buttons */}
          <div className="editor-onboarding-tour__actions">
            <button type="button" className="editor-onboarding-tour__skip" onClick={handleSkip}>
              Пропустить
            </button>
            <button type="button" className="editor-onboarding-tour__btn" onClick={handleNext}>
              {isLast ? 'Завершить' : 'Далее →'}
            </button>
          </div>
        </div>

        <EditorOnboardingTourPointer side={pointerSide} />
      </div>

      {celebrating && <CelebrationOverlay onDone={finishTour} />}
    </div>
  );
}
